/**
 * Dựng nhân vật paper-doll: 1 bộ xương + các mảnh gắn vào xương có sẵn.
 *
 * PHỤ KIỆN ĐÃ XOÁ HẲN (người dùng chốt) ⇒ rig vector nay dùng MẶC ĐỊNH CỦA CLASS:
 * áo màu class · chân đất · đầu trọc. Dáng khác do người dùng thả ảnh 3×3 ở tab Trang phục
 * (xem `data/wardrobe.ts` + `loadAiSkillForm`).
 */
import { Rig, type PartInstance } from '../rig/rig';
import { PartFactory } from './parts';
import { CLASSES, type ClassId } from '../data/classes';
import type { AiPart, AiPartSet } from './ai-parts';

export const RIG_LAYOUT = {
  hipY: -38,
  torsoLen: 30,
  headOffsetY: -30,
  headSize: 30,
  shoulderX: 12,
  shoulderY: -26,
  upperArm: 15,
  foreArm: 14,
  hand: 9,
  hipX: 8,
  thigh: 20,
  shin: 18,
  foot: 9,
};

/** Chiều cao nhân vật trong game (px) — art vector và art AI phải khớp nhau. */
export const TARGET_CHAR_HEIGHT = 104;

const SKIN = 0xf1c7a0;
const SKIN_SHADE_TINT = 0;

export interface CharacterSpec {
  classId: ClassId;
  /** ghi đè màu da (dùng cho quái) */
  skin?: number;
  /** ghi đè màu trang phục */
  outfitColor?: number;
}

/** Dựng rig đầy đủ cho 1 nhân vật. Gọi 1 lần khi tạo unit. */
export function buildCharacterRig(factory: PartFactory, spec: CharacterSpec): Rig {
  const cls = CLASSES[spec.classId];
  const skin = spec.skin ?? SKIN;
  const L = RIG_LAYOUT;
  const rig = new Rig();

  // ---- xương
  rig.addBone('root', null, 0, 0);
  rig.addBone('torso', 'root', 0, L.hipY);
  rig.addBone('head', 'torso', 0, L.headOffsetY);

  rig.addBone('shoulderL', 'torso', -L.shoulderX, L.shoulderY);
  rig.addBone('elbowL', 'shoulderL', 0, L.upperArm);
  rig.addBone('handL', 'elbowL', 0, L.foreArm);
  rig.addBone('shoulderR', 'torso', L.shoulderX, L.shoulderY);
  rig.addBone('elbowR', 'shoulderR', 0, L.upperArm);
  rig.addBone('handR', 'elbowR', 0, L.foreArm);

  rig.addBone('hipL', 'root', -L.hipX, L.hipY);
  rig.addBone('kneeL', 'hipL', 0, L.thigh);
  rig.addBone('footL', 'kneeL', 0, L.shin);
  rig.addBone('hipR', 'root', L.hipX, L.hipY);
  rig.addBone('kneeR', 'hipR', 0, L.thigh);
  rig.addBone('footR', 'kneeR', 0, L.shin);

  /*
   * ---- TRANG PHỤC MẶC ĐỊNH CỦA CLASS (phụ kiện đã xoá hẳn)
   * Áo lấy MÀU CLASS (nhìn là biết class nào), chân đất, đầu trọc. Muốn dáng khác thì dùng
   * tab TRANG PHỤC (ảnh 3×3 của người dùng) — lúc đó rig dựng từ ảnh, không đi đường này.
   */
  const outfit = { style: 'tunic', color: cls.color, accent: cls.accent };
  const outfitColor = spec.outfitColor ?? outfit.color;
  const shoes = { style: 'bare', color: skin, accent: 0xc98f5e };
  const hair = { style: 'bald', color: 0x1f2937, accent: 0x374151 };
  // vẫn khai `color`/`accent` để nhánh vẽ mũ/ kính dùng chung kiểu dữ liệu (dù style 'none'
  // thì phần đó KHÔNG được thêm vào rig)
  const hat = { style: 'none', color: 0x000000, accent: 0x000000 };
  const glasses = { style: 'none', color: 0x000000, accent: 0x000000 };

  const pants = outfitColor;

  // chân (vẽ trước)
  for (const side of ['L', 'R'] as const) {
    rig.addPart({
      bone: `hip${side}`, z: 20, slot: 'legs',
      key: factory.get({ shape: 'limb', variant: 'thigh', color: pants, accent: outfit.accent, skin, w: 11, h: L.thigh }),
      ox: 0, oy: L.thigh / 2, sx: 1, sy: 1,
    });
    rig.addPart({
      bone: `knee${side}`, z: 21, slot: 'legs',
      key: factory.get({ shape: 'limb', variant: 'shin', color: skin, accent: outfit.accent, skin, w: 10, h: L.shin }),
      ox: 0, oy: L.shin / 2, sx: 1, sy: 1,
    });
    rig.addPart({
      bone: `foot${side}`, z: 10, slot: 'shoes',
      key: factory.get({ shape: 'foot', variant: shoes.style, color: shoes.color, accent: shoes.accent, skin, w: 17, h: L.foot + 4 }),
      ox: 0, oy: L.foot / 2, sx: 1, sy: 1,
    });
  }

  // thân
  rig.addPart({
    bone: 'torso', z: 30, slot: 'outfit',
    key: factory.get({ shape: 'torso', variant: outfit.style, color: outfitColor, accent: outfit.accent, skin, w: 28, h: L.torsoLen }),
    ox: 0, oy: -L.torsoLen / 2 + 4, sx: 1, sy: 1,
  });

  // tay
  for (const side of ['L', 'R'] as const) {
    rig.addPart({
      bone: `shoulder${side}`, z: 25, slot: 'arms',
      key: factory.get({ shape: 'limb', variant: 'arm', color: outfitColor, accent: outfit.accent, skin, w: 9, h: L.upperArm }),
      ox: 0, oy: L.upperArm / 2, sx: 1, sy: 1,
    });
    rig.addPart({
      bone: `elbow${side}`, z: 26, slot: 'arms',
      key: factory.get({ shape: 'limb', variant: 'fore', color: skin, accent: outfit.accent, skin, w: 8.5, h: L.foreArm }),
      ox: 0, oy: L.foreArm / 2, sx: 1, sy: 1,
    });
    rig.addPart({
      bone: `hand${side}`, z: 27, slot: 'arms',
      key: factory.get({ shape: 'limb', variant: 'hand', color: skin, accent: outfit.accent, skin, w: 9, h: L.hand }),
      ox: 0, oy: L.hand / 2 - 1, sx: 1, sy: 1,
    });
  }

  // đầu
  rig.addPart({
    bone: 'head', z: 50, slot: 'head',
    key: factory.get({ shape: 'head', variant: 'face', color: skin, accent: cls.accent, skin, w: L.headSize, h: L.headSize + 2 }),
    ox: 0, oy: -L.headSize / 2, sx: 1, sy: 1,
  });

  // phụ kiện đầu — mỗi cái là 1 mảnh, gắn vào xương đầu
  rig.addPart({
    bone: 'head', z: 60, slot: 'hair',
    key: factory.get({ shape: 'hair', variant: hair.style, color: hair.color, accent: hair.accent, skin, w: 36, h: 34 }),
    ox: 0, oy: -L.headSize / 2 + 3, sx: 1, sy: 1,
  });
  if (glasses.style !== 'none') {
    rig.addPart({
      bone: 'head', z: 70, slot: 'glasses',
      key: factory.get({ shape: 'glasses', variant: glasses.style, color: glasses.color, accent: glasses.accent, skin, w: 30, h: 18 }),
      ox: 0, oy: -L.headSize / 2 + 1, sx: 1, sy: 1,
    });
  }
  if (hat.style !== 'none') {
    rig.addPart({
      bone: 'head', z: 80, slot: 'hat',
      key: factory.get({ shape: 'hat', variant: hat.style, color: hat.color, accent: hat.accent, skin, w: hat.style === 'bandana' ? 30 : 38, h: 26 }),
      ox: 0, oy: -L.headSize / 2 - 6, sx: 1, sy: 1,
    });
  }

  void SKIN_SHADE_TINT;
  return rig;
}

/** Dựng rig cho QUÁI — dùng cùng hệ xương nhưng không có phụ kiện. */
export function buildEnemyRig(
  factory: PartFactory,
  opts: { color: number; accent: number; radius: number; isBoss?: boolean },
): Rig {
  const rig = new Rig();
  const scale = Math.max(0.55, opts.radius / 20);
  const h = 30 * scale;
  const limb = 15 * scale;
  const skin = opts.color;

  rig.addBone('root', null, 0, 0);
  rig.addBone('torso', 'root', 0, -38 * scale);
  rig.addBone('head', 'torso', 0, -26 * scale);
  rig.addBone('shoulderL', 'torso', -11 * scale, -22 * scale);
  rig.addBone('elbowL', 'shoulderL', 0, limb);
  rig.addBone('shoulderR', 'torso', 11 * scale, -22 * scale);
  rig.addBone('elbowR', 'shoulderR', 0, limb);
  rig.addBone('hipL', 'root', -7 * scale, -38 * scale);
  rig.addBone('kneeL', 'hipL', 0, limb);
  rig.addBone('footL', 'kneeL', 0, limb);
  rig.addBone('hipR', 'root', 7 * scale, -38 * scale);
  rig.addBone('kneeR', 'hipR', 0, limb);
  rig.addBone('footR', 'kneeR', 0, limb);

  const parts: PartInstance[] = [
    { bone: 'hipL', z: 20, key: factory.get({ shape: 'limb', variant: 'thigh', color: opts.color, accent: opts.accent, skin, w: 11 * scale, h: limb }), ox: 0, oy: limb / 2, sx: 1, sy: 1 },
    { bone: 'kneeL', z: 21, key: factory.get({ shape: 'limb', variant: 'shin', color: opts.color, accent: opts.accent, skin, w: 10 * scale, h: limb }), ox: 0, oy: limb / 2, sx: 1, sy: 1 },
    { bone: 'hipR', z: 20, key: factory.get({ shape: 'limb', variant: 'thigh', color: opts.color, accent: opts.accent, skin, w: 11 * scale, h: limb }), ox: 0, oy: limb / 2, sx: 1, sy: 1 },
    { bone: 'kneeR', z: 21, key: factory.get({ shape: 'limb', variant: 'shin', color: opts.color, accent: opts.accent, skin, w: 10 * scale, h: limb }), ox: 0, oy: limb / 2, sx: 1, sy: 1 },
    { bone: 'footL', z: 10, key: factory.get({ shape: 'foot', variant: 'cloth', color: 0x3f3f46, accent: opts.accent, skin, w: 14 * scale, h: 9 * scale }), ox: 0, oy: 4 * scale, sx: 1, sy: 1 },
    { bone: 'footR', z: 10, key: factory.get({ shape: 'foot', variant: 'cloth', color: 0x3f3f46, accent: opts.accent, skin, w: 14 * scale, h: 9 * scale }), ox: 0, oy: 4 * scale, sx: 1, sy: 1 },
    { bone: 'torso', z: 30, key: factory.get({ shape: 'torso', variant: 'tunic', color: opts.color, accent: opts.accent, skin, w: 26 * scale, h }), ox: 0, oy: -h / 2 + 4, sx: 1, sy: 1 },
    { bone: 'shoulderL', z: 25, key: factory.get({ shape: 'limb', variant: 'arm', color: skin, accent: opts.accent, skin, w: 9 * scale, h: limb }), ox: 0, oy: limb / 2, sx: 1, sy: 1 },
    { bone: 'elbowL', z: 26, key: factory.get({ shape: 'limb', variant: 'fore', color: skin, accent: opts.accent, skin, w: 8 * scale, h: limb }), ox: 0, oy: limb / 2, sx: 1, sy: 1 },
    { bone: 'shoulderR', z: 25, key: factory.get({ shape: 'limb', variant: 'arm', color: skin, accent: opts.accent, skin, w: 9 * scale, h: limb }), ox: 0, oy: limb / 2, sx: 1, sy: 1 },
    { bone: 'elbowR', z: 26, key: factory.get({ shape: 'limb', variant: 'fore', color: skin, accent: opts.accent, skin, w: 8 * scale, h: limb }), ox: 0, oy: limb / 2, sx: 1, sy: 1 },
    { bone: 'head', z: 50, key: factory.get({ shape: 'head', variant: 'face', color: skin, accent: opts.accent, skin, w: 28 * scale, h: 29 * scale }), ox: 0, oy: -14 * scale, sx: 1, sy: 1 },
  ];
  if (opts.isBoss) {
    parts.push({
      bone: 'head', z: 80,
      key: factory.get({ shape: 'hat', variant: 'straw', color: 0x3f3f46, accent: 0x18181b, skin, w: 40 * scale, h: 24 * scale }),
      ox: 0, oy: -18 * scale, sx: 1, sy: 1,
    });
  }
  for (const p of parts) rig.addPart(p);
  return rig;
}


/**
 * DỰNG NHÂN VẬT TỪ ẢNH AI.
 * Khác biệt cốt lõi: TỈ LỆ do ảnh quyết định — ảnh tay dài bao nhiêu thì xương dài bấy nhiêu.
 * (Art vector thì tỉ lệ nằm trong hằng số RIG_LAYOUT; art AI thì không cần căn tay.)
 *
 * Dùng chung cho CẢ nhân vật lẫn quái: chúng chỉ khác `targetHeight` (quái nhỏ hơn,
 * boss to gấp đôi). Không có tham số nào phụ thuộc class ở đây ⇒ 1 hàm cho tất cả.
 */
export function buildAiRigFromParts(
  factory: PartFactory,
  ai: AiPartSet,
  opts: { targetHeight?: number; scaleMult?: number } = {},
): Rig {
  const rig = new Rig();

  /*
   * CHUẨN HOÁ TỈ LỆ.
   * Ảnh AI vẽ tay/chân dài ngắn tuỳ hứng; lấy thẳng chiều cao ảnh làm chiều dài xương
   * thì nhân vật dễ thành "vượn". Nên lấy THÂN làm gốc, chi dài theo tỉ lệ vàng,
   * rồi co/giãn ảnh cho vừa ⇒ giữ nét vẽ AI, dáng luôn đúng.
   */
  const torsoLen = ai.torso.h;
  const torsoW = ai.torso.w;
  const headH = ai.head.h;
  const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));
  const fit = (partH: number, target: number): { len: number; scale: number } => {
    const scale = clamp(target / Math.max(1, partH), 0.25, 2.4);
    return { len: partH * scale, scale };
  };

  /*
   * CHÂN — ĐO THỰC TẾ TRÊN CẢ 13 SHEET AI, KHÔNG ĐOÁN:
   *
   *   · Ô "cẳng chân" (ô 6) LUÔN là **CẢ MỘT CHÂN** (hông → mắt cá), cao gấp ~2,2 lần
   *     bề ngang (85×192 … 88×192), không phải mỗi đoạn cẳng chân.
   *   · Ô "quần" (ô 5) thì lúc là **cả đôi quần** (thân quần liền một khối ở giữa —
   *     soi bản đồ alpha thấy kín, KHÔNG có khe giữa hai ống), lúc là **một chân trần**
   *     (Đẻ mướn mặc váy). Nghĩa là nó LUÔN trùng nội dung với ô chân.
   *
   * ⇒ Dán ô quần làm mảnh hông TĨNH là sai: nó chồng lên chính hai chân đang cử động —
   *   đúng lỗi người chơi báo "cẳng chân vào phần đùi bị chồng lên nhau".
   *   Cách đúng: dùng ô CHÂN làm cả chân, BỎ ô quần. Có ô đùi riêng thì đi đường 2 khúc.
   */
  const shinIsWholeLeg = !ai.thigh && ai.shin.h >= ai.shin.w * 1.7;
  /** tỉ lệ chiều dài chân so với thân — dùng cho cả ước lượng cỡ ủng bên dưới */
  const legRatio = shinIsWholeLeg ? 0.95 : 0.58;
  const shin = fit(ai.shin.h, torsoLen * legRatio);
  const thigh = ai.thigh ? fit(ai.thigh.h, torsoLen * 0.66) : null;
  /*
   * ĐIỂM MẮT CÁ **THẬT** CỦA MẢNH CHÂN (tính từ gốc xương của chính mảnh đó).
   *
   * ⚠️ Bẫy người chơi báo "bàn chân hơi hở và lệch một chút xíu": trước đây tôi lấy
   * `shin.len` (= CHIỀU CAO HỘP BAO của ảnh) làm vị trí mắt cá. Nhưng điểm khớp được
   * dò theo BAN NHẠC 9% trên/ dưới của mảnh, mà ban nhạc không nằm sát mép hộp bao ⇒
   * mắt cá thật NGẮN HƠN `shin.len` ~9% chiều cao ảnh. Đo được trên game: ủng bị đặt
   * **hở 2,4px** và **lệch ngang 3,9px** so với mắt cá.
   * Cách đúng (đã dùng cho bàn tay ↔ cánh tay): lấy ĐÚNG điểm đáy thật của mảnh chân.
   */
  const shinEndX = (-ai.shin.topX + ai.shin.botX) * shin.scale;
  /*
   * CHÂN DỊCH XUỐNG — GIỮ NGUYÊN CHIỀU DÀI, CHỈ HẠ VỊ TRÍ.
   *
   * Người dùng: *"Giờ kéo chân của các class xuống 1 chút"* → *"Không tăng chiều dài, kéo chân dịch
   * xuống rồi neo lại"*.
   *
   * ⚠️⚠️ VÌ SAO KHÔNG DÙNG CÁCH NÀO KHÁC:
   *   · KHÔNG hạ `hipY`: nó là gốc của CẢ xương hông LẪN xương chân (`addBone('hipL','root',…)`) ⇒
   *     hông và cạp quần TỤT THEO, vừa sửa xong chuyện hở eo là hỏng lại ngay.
   *   · KHÔNG nhân hệ số vào `shinEndY`: đó là TĂNG CHIỀU DÀI (người dùng đã bác).
   *
   * CÁCH ĐÚNG: giữ `shinEndY` nguyên (chiều dài cẳng chân không đổi), chỉ CỘNG thêm một khoảng
   * dịch `LEG_DROP` vào XƯƠNG HÔNG CỦA CHÂN (`legY = hipY + LEG_DROP`) ⇒ cả chuỗi chân trượt
   * xuống nguyên khối, chiều dài từng khúc giữ nguyên.
   */
  const LEG_DROP = 30;
  const shinEndY = (-ai.shin.topY + ai.shin.botY) * shin.scale;
  const legLen = (thigh?.len ?? 0) + shinEndY;

  // ---- tay: ưu tiên 2 khúc; nếu ảnh vẽ liền 1 mảnh thì dùng 1 khúc
  const singleArm = !ai.upperArm || !ai.foreArm;
  /*
   * ⚠️⚠️ Ô TAY ĐÚNG LÀ Ô 4 (r2c1) — "CẢ CÁNH TAY + nắm đấm", KHÔNG phải ô 3.
   * Người dùng chốt: *"bỏ bàn tay, thay cánh tay bằng cả cánh tay ở ảnh 3x3 dòng 2 cột 1"*.
   * `CELL_ORDER` trong `ai-parts.ts` đã gán ô 4 → `arm` và để ô 3 + ô 8 (`hand`) thành `empty`,
   * nên ở đây chỉ cần dùng `ai.arm`; KHÔNG còn `ai.hand` ⇒ nhánh dán bàn tay bên dưới tự bỏ qua.
   */
  const armPart: AiPart = singleArm ? ai.arm ?? ai.foreArm! : ai.upperArm!;
  /** tỉ lệ nhấc cánh tay lên để mép trên chui vào sau thân, che khe hở khớp vai */
  const ARMS_TUCK = 0.06;
  /*
   * CHIỀU DÀI TAY LIỀN MỘT KHÚC = **1,20 × thân** (ĐO, không mò).
   *
   * Mảnh ô 4 nay gồm CẢ nắm đấm (xem `CELL_OVERFLOW_DOWN` trong `ai-parts.ts`): cao **351px**
   * (tay 322 + 29px nắm đấm nằm dưới mốc ô) trên thân **292px** ⇒ tay/thân = **1,20** (cả 5 sheet).
   * Số cũ `1,10` chỉ đúng cho mảnh tay đã bị CẮT CỤT ở cổ tay; nay có thêm nắm đấm nên phải 1,20
   * mới giữ đúng tầm với của bản vẽ (và khớp với bản cũ 0,86 tay + 0,24 bàn tay = 1,10… ở cổ tay,
   * cộng thêm phần nắm đấm phía dưới).
   */
  const ARM_FULL_RATIO = 1.2;
  const armTarget = singleArm ? torsoLen * ARM_FULL_RATIO : torsoLen * 0.5;
  const armFit = fit(armPart.h, armTarget);
  const foreArmFit = !singleArm && ai.foreArm ? fit(ai.foreArm.h, torsoLen * 0.46) : null;

  /*
   * CỠ ỦNG: đừng suy từ bề rộng thân (dễ ra ủng khổng lồ 26px cho nhân vật 104px).
   * Ước lượng chiều cao nhân vật rồi lấy ủng ≈ 11.5% chiều cao đó.
   *   T = torsoLen + headH + 0.58·torsoLen + 0.115·T  ⇒  T = (1.58·torsoLen + headH)/0.885
   */
  const estTotal = ((legRatio + 1) * torsoLen + headH) / 0.885;
  /*
   * ĐỘ LỆCH TÂM CỦA MẢNH TAY — phải tính MỘT LẦN rồi dùng cho cả mảnh tay LẪN bàn tay.
   * Lỗi đã gặp: khi đặt bàn tay tôi chỉ lấy `botY * scale` mà QUÊN cộng độ lệch tâm này,
   * nên bàn tay bị kéo lên nằm giữa cẳng tay.
   */
  const armOx = -armPart.topX * armFit.scale;
  const armOy = -armPart.topY * armFit.scale - armFit.len * ARMS_TUCK * 0.5;
  /** điểm CUỐI cánh tay trong hệ của xương vai */
  const armEndX = armOx + armPart.botX * armFit.scale;
  const armEndY = armOy + armPart.botY * armFit.scale;

  const bootScale = clamp((estTotal * 0.115) / Math.max(1, ai.foot.h), 0.15, 2.2);
  const bootH = ai.foot.h * bootScale;

  /*
   * ĐẦU CHÚI XUỐNG MỘT CHÚT (headSink) ĐỂ CẮM VÀO VAI.
   *
   * ⚠️ Bẫy người chơi báo: "đầu hơi bị xa thân (đầu lìa khỏi cổ)". Ảnh đầu do AI vẽ
   * KHÔNG có cổ, và hộp bao của nó kết thúc đúng ở CẰM; đặt cằm ngay trên đường vai
   * thì mắt đọc ra "cái đầu lơ lửng". Hạ đầu xuống ~5% chiều cao thân ⇒ cằm cắm vào
   * vai, đọc ra "đầu ngồi trên cổ" mà không cần vẽ thêm mảnh cổ nào.
   */
  /*
   * ⚠️ ĐỔI TỪ 0.05 → 0.02 (người dùng: "đầu character hơi bị thụt về sau lưng chút xíu").
   *
   * LỊCH SỬ (3 mức, ghi lại để không lặp):
   *   · 0   — người chơi báo *"đầu lìa khỏi cổ"* (ảnh đầu AI vẽ KHÔNG có cổ, hộp bao kết thúc đúng ở cằm).
   *   · 5%  — quá tay: đầu bị kéo xuống, nhìn như THỤT VỀ SAU LƯNG.
   *   · 2%  — bản trước; nhưng ĐO LẠI bằng `dauDay - thanDinh` trên cả 5 class: **hở 4px**
   *           (`torsoLen` thật của art AI chỉ 34–37px ⇒ 2% = 0,7px, không đủ bù khe 4px).
   *           Người dùng: *"các class cần cho hạ thấp đầu xuống 1 chút, đang hở giữa đầu và thân"*.
   *
   * ⚠️ DẤU: `oy` của sprite tính theo trục Y HƯỚNG XUỐNG, nhưng `headSink` được CỘNG vào
   * `oy = -botY + headSink` ⇒ giá trị DƯƠNG đẩy đầu LÊN TRÊN (đo được: 2% ⇒ hở 4px, 15% ⇒ hở 9px,
   * tức tăng sink làm hở THÊM). Muốn HẠ đầu xuống phải dùng dấu ÂM.
   *
   * NAY `-15%`: với `torsoLen` 34–37px ⇒ hạ **5,1–5,6px**, vừa đủ bù khe 4px và chồng vào vai ~1px.
   * ⚠️ Dùng TỈ LỆ chứ không hằng số px: `torsoLen` khác nhau theo từng class (34–37) nên hằng số sẽ
   * hở ở class này và thụt ở class khác.
   *
   * Đo lại sau khi sửa: `hoDauThan` phải **≤ 0** (âm = chồng vào nhau) ở CẢ 5 class.
   */
  /*
   * ⚠️⚠️ ĐỔI TỪ TỈ LỆ SANG SỐ PX CỐ ĐỊNH — người dùng: *"để đầu xuống sát với thân thêm 5px"*.
   *
   * `torsoLen * 0.02` chỉ ~0,7px (torsoLen 34–37) nên không đủ để thấy khác. Nay trừ thẳng 5px.
   * Dùng SỐ PX (không phải tỉ lệ) vì `torsoLen` chỉ chênh nhau 3px giữa các class — sai số nhỏ hơn
   * nhiều so với mức dịch 5px mà người dùng yêu cầu, và đúng nghĩa "thêm 5px".
   *
   * ⚠️ DẤU: `oy = -botY + headSink` theo trục Y HƯỚNG XUỐNG ⇒ giá trị DƯƠNG đẩy đầu LÊN (đã đo:
   * 2% ⇒ hở 4px, 15% ⇒ hở 9px). Muốn HẠ đầu xuống sát thân phải TRỪ.
   */
  /*
   * ⚠️⚠️ THEO LỆNH NGƯỜI DÙNG: *"để lại khoảng cách giữa đầu và thân, sau đó tăng thêm"*.
   *
   * Đã đo bằng pixel thật (canvas alpha, không phải hộp bao sprite):
   * `oy = -botY + headSink` theo trục Y HƯỚNG XUỐNG:
   *   · headSink ÂM ⇒ `oy` nhỏ hơn ⇒ sprite đầu bị kéo lên trên ⇒ đầu DÍNH SÁT/ chồng vào thân.
   *   · headSink DƯƠNG ⇒ `oy` lớn hơn ⇒ sprite đầu bị đẩy xuống ⇒ TÁCH RỜI, để LẠI KHOẢNG CÁCH.
   *
   * Người dùng muốn CÓ khoảng cách đầu↔thân và TĂNG THÊM nữa ⇒ dùng giá trị DƯƠNG.
   * `+15` = tăng thêm 5px so với mức `+10` (người dùng: *"Thêm 5px nữa"*).
   */
  const headSink = 20;

  const hipY = -(legLen + bootH * 0.45);
  const hipX = Math.max(3, torsoW * 0.19);
  /*
   * VAI: đặt hơi LÙI VÀO TRONG thân (0.40, không phải 0.46).
   * Mảnh tay trong ảnh AI là tay GẬP nên hộp bao của nó không đối xứng — nếu đặt
   * sát mép thân thì mép trên của tay không chui được vào sau thân ⇒ HỞ KHỚP VAI.
   * Lùi vào + nhấc lên một chút (xem ARMS_TUCK) là kín khớp.
   */
  const shoulderX = Math.max(5, torsoW * 0.4);
  const neckY = -(torsoLen * 0.97);
  const shoulderY = -(torsoLen * 0.64);

  // ---- xương
  rig.addBone('root', null, 0, 0);
  rig.addBone('torso', 'root', 0, hipY);
  rig.addBone('head', 'torso', 0, neckY);
  rig.addBone('shoulderL', 'torso', -shoulderX, shoulderY);
  rig.addBone('shoulderR', 'torso', shoulderX, shoulderY);
  if (!singleArm) {
    rig.addBone('elbowL', 'shoulderL', 0, armFit.len);
    rig.addBone('elbowR', 'shoulderR', 0, armFit.len);
    rig.addBone('handL', 'elbowL', 0, foreArmFit!.len);
    rig.addBone('handR', 'elbowR', 0, foreArmFit!.len);
  }
  /*
   * ⚠️⚠️ XƯƠNG HÔNG CỦA CHÂN ĐƯỢC HẠ RIÊNG — ĐÂY MỚI LÀ CHỖ ĐÚNG ĐỂ "DỊCH CHÂN XUỐNG".
   *
   * LỖI TÔI ĐÃ MẮC (người dùng chỉ ra): *"Tôi thấy bạn tăng mà phần khớp đùi vẫn sát tới dưới
   * body"*. Bản trước tôi cộng `LEG_DROP` vào offset của `kneeL` (`thigh.len + LEG_DROP`) trong khi
   * `hipL` vẫn nằm ở `hipY` ⇒ **khớp hông KHÔNG nhúc nhích**, chỉ có đầu gối bị đẩy xuống
   * ⇒ thực chất là **KÉO DÀI ĐÙI**, đúng thứ người dùng bác, và mắt vẫn thấy đùi dính dưới thân.
   *
   * Nay: `hipL/hipR` (xương hông của CHÂN) hạ xuống `hipY + LEG_DROP`, còn mọi chiều dài khúc
   * (`thigh.len`, `shinEndY`) GIỮ NGUYÊN ⇒ cả chuỗi chân trượt xuống nguyên khối, khớp đùi rời khỏi
   * đáy thân đúng như yêu cầu.
   *
   * ⚠️ `hipY` của THÂN/HÔNG vẫn giữ nguyên (xem dòng `rig.addBone('torso','root',0,hipY)`), nên cạp
   * quần vừa sửa không bị tụt lại.
   */
  const legY = hipY + LEG_DROP;
  /*
   * DỊCH 2 CHÂN SANG PHẢI — người dùng: *"dịch 2 chân sang bên phải ảnh 1 tí"*.
   *
   * ⚠️ Chỉ cộng vào X của xương hông CHÂN (`hipL/hipR`), KHÔNG đụng `hipX` chung: `hipX` còn dùng
   * cho mảnh hông/ cạp quần và vùng bẹn. Dịch cả hai xương cùng một lượng ⇒ hai chân trượt ngang
   * nguyên khối, khoảng cách giữa chúng không đổi (không bị dạng/ khép chân).
   *
   * `LEG_X_SHIFT = 2` (đơn vị thiết kế, trước khi chuẩn hoá `k`) ⇒ khoảng 1,5–2px trên màn hình.
   */
  const LEG_X_SHIFT = 2;
  rig.addBone('hipL', 'root', -hipX + LEG_X_SHIFT, legY);
  rig.addBone('hipR', 'root', hipX + LEG_X_SHIFT, legY);
  if (thigh) {
    rig.addBone('kneeL', 'hipL', 0, thigh.len);
    rig.addBone('kneeR', 'hipR', 0, thigh.len);
    // xương cổ chân nằm ở MẮT CÁ THẬT (không phải chiều cao hộp bao) — xem `shinEndY`
    rig.addBone('footL', 'kneeL', 0, shinEndY);
    rig.addBone('footR', 'kneeR', 0, shinEndY);
  } else {
    // xương cổ chân nằm ở MẮT CÁ THẬT (không phải chiều cao hộp bao) — xem `shinEndY`
    rig.addBone('footL', 'hipL', 0, shinEndY);
    rig.addBone('footR', 'hipR', 0, shinEndY);
  }

  /*
   * ---- HÔNG (ô 5 = hàng 2 cột 2, đã cắt sẵn tới CHỮ V BẸN ở `ai-parts.ts`).
   *
   * Người dùng: *"các nhân vật thêm phần hông (chỉ cắt từ thắt lưng đến và lấy phần chữ V bẹn)
   * … cắt từ ảnh 3x3 hàng 2 cột 2"*.
   *
   * VẤN ĐỀ: hai chân là hai mảnh RIÊNG (ô 6) nên giữa chúng ở vùng bẹn HỞ một lỗ nhìn xuyên
   * xuống nền. Ô 5 vẽ đúng vùng quần/hông đó ⇒ dùng nó để lấp.
   *
   * ⚠️⚠️ THỨ TỰ VẼ — NGƯỜI DÙNG CHỐT: *"cho phần thắt lưng index behind body và front 2 cái chân"*
   *   · **TRÊN 2 CHÂN**  (`z = 24` > chân `22`) ⇒ hông/quần trùm lên hai ống chân, đúng như quần thật.
   *   · **DƯỚI THÂN**    (`z = 24` < thân `30`)  ⇒ gấu áo phủ lên thắt lưng, không thấy đường nối.
   * Thứ tự đầy đủ: ủng 9 → chân 22 → nắp khớp hông 23 → **HÔNG 24** → tay phải 25 → nắp vai 26 →
   * thân 30 → tay trái 34 → đầu 50.
   *
   * ⚠️ VỊ TRÍ: đỉnh mảnh hông phải TRÙNG ĐÁY THÂN. Thân vẽ trên xương `torso` (ở `hipY`) với
   * `oy = -torsoLen/2 + 2` ⇒ **đáy thân ở `hipY + 2`**.
   *
   * ⚠️⚠️ ĐÃ DỊCH LÊN — người dùng: *"phần cạp quần (phần hông chữ V ở bẹn) cho dịch lên 1 tí, nhân
   * vật bị thủng bụng"*.
   * LỖI CŨ: đặt đỉnh mảnh hông ở `hipY - 2` (DƯỚI đáy thân 4px) ⇒ giữa gấu áo và cạp quần hở một
   * khoảng, nhìn như **thủng bụng** (đo trên ảnh `rig-5class.png`: Gái ngành hở khoảng trắng ngay
   * trên cạp). Nay đặt ở `hipY + 8`, tức CAO hơn đáy thân 6px ⇒ cạp quần chui lên dưới gấu áo, phần
   * chồng bị thân (z = 30) che nên không thấy đường nối.
   *
   * ⚠️ ĐO LẠI BẰNG `tools/rig-classes.mjs` (số thật, không phải hộp bao sprite):
   *   ở mức `hipY + 8`: khe **thân↔quần = +1,06 đến +1,11px** — vẫn HỞ. Người dùng: *"kéo phần hông
   *   chữ V lên trên 1 chút nhé, đang khớp với thân"*.
   * Nay `hipY - 6` (kéo lên ~7px so với bản `hipY + 8` đầu tiên) — khe âm ~5,5px.
   *
   * ⚠️⚠️ BÀI HỌC LỚN: người dùng *"Kéo thêm nữa đi, với mắt người dùng tôi thấy vẫn hở"* — khe
   * đo được đã ÂM (-0,55px) mà MẮT vẫn thấy hở. Lý do: số đo là khoảng cách HỘP BAO giữa hai mảnh,
   * còn thị giác đọc theo VIỀN VẼ (đường viền đen của áo và cạp). Hai mảnh có thể chồng hộp bao
   * nhưng viền đen vẫn tạo một vệt sáng ở giữa ⇒ mắt thấy "hở". Vì vậy với việc THỊ GIÁC, phải
   * kéo dư ra (chồng nhiều) rồi kiểm bằng ẢNH, không chốt theo số.
   *
   * ⚠️ BÀI HỌC ĐO: 5 lần trước tôi đo bằng `sprite.getBounds()` của Phaser — hộp bao GỒM CẢ vùng
   * trong suốt nên luôn báo "chồng nhau" dù mắt thấy hở. Phải đo bằng pixel alpha thật trên atlas
   * (đúng cách `rig-classes.mjs` làm), HOẶC nhìn ảnh phóng to.
   *
   * ⚠️ KHÔNG còn điều kiện `!shinIsWholeLeg`: trước đây ô quần bị BỎ khi ô chân là cả chân, nên
   * với `mother` (ô 5 là MỘT ĐÙI trần, ô 6 là cẳng chân trần) cả hai đều bị bỏ ⇒ hở hẳn vùng bẹn.
   * Nay luôn vẽ dải hông; riêng `mother` mảnh đó là đùi trần nên chỉ lấp được một phần — muốn đủ
   * thì phải sinh lại sheet có quần (đã báo người dùng).
   */
  if (ai.pants) {
    rig.addPart({
      bone: 'root', z: 24, slot: 'pelvis', key: ai.pants.key,
      ox: 0, oy: hipY - 14 + ai.pants.h / 2, sx: 1, sy: 1,
    });
  }

  // ---- chân
  // LƯU Ý: xương `foot` nằm ở CỔ CHÂN, nên cẳng chân phải gắn vào xương ĐẦU GỐI
  // (2 khúc) hoặc xương HÔNG (1 khúc) — gắn nhầm vào xương cổ chân sẽ làm ủng tách rời.
  const shinBone = thigh ? 'knee' : 'hip';
  for (const side of ['L', 'R'] as const) {
    if (thigh && ai.thigh) {
      rig.addPart({
        bone: `hip${side}`, z: 21, slot: 'legs', key: ai.thigh.key,
        ox: -ai.thigh.topX * thigh.scale, oy: -ai.thigh.topY * thigh.scale,
        sx: thigh.scale, sy: thigh.scale,
      });
    }
    rig.addPart({
      bone: `${shinBone}${side}`, z: 22, slot: 'legs', key: ai.shin.key,
      ox: -ai.shin.topX * shin.scale, oy: -ai.shin.topY * shin.scale,
      sx: shin.scale, sy: shin.scale,
    });
    /*
     * ỦNG — CANH ĐIỂM–ĐIỂM, KHÔNG DỰA VÀO GỐC XƯƠNG:
     *
     *   · DỌC: xương cổ chân ĐÃ được đặt đúng tại MẮT CÁ THẬT (`shinEndY`, xem phần
     *     xương phía trên) nên chỉ cần gắn đỉnh ủng vào gốc xương: `-foot.topY * bootScale`.
     *     ⚠️ ĐỪNG cộng thêm `shinEndY` ở đây — làm thế là tính chiều dài chân HAI LẦN
     *     và ủng tụt xuống ~31px (đã thử và đo được đúng như vậy).
     *   · NGANG: mắt cá thật của mảnh chân lệch khỏi gốc xương theo trục x một khoảng
     *     `shinEndX` ⇒ phải bù vào toạ độ ngang, nếu không ủng lệch ngang ~3,9px —
     *     đúng triệu chứng người chơi báo "bàn chân hơi hở và lệch một chút xíu".
     */
    rig.addPart({
      bone: `foot${side}`, z: 9, slot: 'shoes', key: ai.foot.key,
      ox: shinEndX - ai.foot.topX * bootScale,
      oy: -ai.foot.topY * bootScale,
      sx: bootScale, sy: bootScale,
    });
    // NẮP KHỚP HÔNG: che khe hở khi chân vung mạnh (đá, quét cước)
    rig.addPart({
      bone: `hip${side}`, z: 23, slot: 'joints',
      key: factory.get({
        shape: 'joint', variant: 'hip', color: (ai.pants ?? ai.shin).color,
        accent: 0, skin: 0, w: Math.max(6, ai.shin.w * shin.scale * 0.5), h: Math.max(6, ai.shin.w * shin.scale * 0.5),
      }),
      ox: 0, oy: 0, sx: 1, sy: 1,
    });
  }

  // ---- thân
  rig.addPart({ bone: 'torso', z: 30, slot: 'outfit', key: ai.torso.key, ox: 0, oy: -torsoLen / 2 + 2, sx: 1, sy: 1 });

  /*
   * ⚠️⚠️ ĐỘ SÂU (z) CỦA TAY THEO BÊN — người dùng: *"tay phía bên TRÁI ảnh đặt front body"*.
   *
   * `shoulderL` đặt ở x **ÂM** ⇒ đó là nửa **TRÁI của ảnh**; `shoulderR` ở x DƯƠNG ⇒ nửa PHẢI.
   * Thân (`torso`) vẽ ở **z = 30**, nên:
   *   · tay nửa TRÁI ảnh ⇒ **z = 34**: VẼ ĐÈ LÊN thân (đúng yêu cầu "front body");
   *   · tay nửa PHẢI ảnh ⇒ **z = 25**: nằm SAU thân (giữ như cũ).
   * Lưu ý z nhỏ vẽ TRƯỚC (xem `sort((a, b) => a.z - b.z)` trong `unitview.ts` và trong
   * `drawGearPreview` của `hud.ts`) — nên "trước mặt" = z LỚN HƠN.
   * Nắp khớp lấy `armZ(side) + n` để luôn nằm TRÊN mảnh tay của chính bên đó.
   */
  const ARM_Z_BACK = 25; // nửa PHẢI ảnh: sau thân (30)
  const ARM_Z_FRONT = 34; // nửa TRÁI ảnh: trước thân (30), dưới đầu (50)
  const armZ = (side: 'L' | 'R'): number => (side === 'L' ? ARM_Z_FRONT : ARM_Z_BACK);
  /*
   * ⚠️ NẮP KHỚP VAI LUÔN NẰM **SAU THÂN** (`ARM_Z_BACK + 1`), KỂ CẢ bên TRÁI — ĐỪNG đặt nó ngay
   * trên mảnh tay trước mặt.
   *
   * ĐÃ THẤY TRONG ẢNH PREVIEW: khi tay nửa trái ảnh lên trước thân (z 34) mà nắp giữ `z = az + 1`
   * (35) thì nắp thành **một VÒNG TRÒN màu da NẰM TRÊN ÁO** ở vai trái — nhìn như miếng vá.
   * Khe khớp phía TRƯỚC đã do chính MẢNH TAY che (nó vẽ đè lên thân), nên nắp chỉ còn cần thiết
   * cho bên SAU ⇒ để sau thân cho cả hai bên là an toàn và sạch.
   */
  const ARM_CAP_Z = ARM_Z_BACK + 1;

  // ---- tay
  if (singleArm) {
    for (const side of ['L', 'R'] as const) {
      /*
       * ⚠️⚠️ LẬT ĐỐI XỨNG TAY BÊN PHẢI — người dùng: *"tay lật lại kiểu đối xứng ý, vì 2 tay trái nhau"*.
       *
       * Ảnh 3×3 chỉ có MỘT ô tay, code dán cùng mảnh đó cho cả `shoulderL` (x ÂM) lẫn `shoulderR`
       * (x DƯƠNG) ⇒ hai tay GIỐNG HỆT nhau, nhìn ra "hai tay trái". Lật bằng `sx` ÂM.
       *
       * ⚠️ LẬT THÌ PHẢI ĐỔI DẤU `ox` — ĐỪNG CHỈ ĐẶT `sx` ÂM:
       * Sprite vẽ quanh TÂM ảnh, nên điểm ảnh (topX, topY) nằm ở `ox + sx·topX` theo trục ngang.
       *   · không lật: cần `ox + k·topX = 0` ⇒ `ox = -k·topX` = `armOx` (đúng như cũ).
       *   · CÓ lật  : cần `ox + (-k)·topX = 0` ⇒ `ox = +k·topX` = **`-armOx`**.
       * Quên đổi dấu thì KHỚP VAI của tay phải lệch ra ngoài `2·k·topX` px (hở/ lệch khớp).
       */
      const mirror = side === 'R';
      const az = armZ(side);
      rig.addPart({
        bone: `shoulder${side}`, z: az, slot: 'arms', key: armPart.key,
        // dùng ĐIỂM KHỚP THẬT của mảnh tay (không phải tâm hộp bao) ⇒ hết hở khớp khi vung tay
        ox: mirror ? -armOx : armOx,
        oy: armOy,
        sx: mirror ? -armFit.scale : armFit.scale,
        sy: armFit.scale,
      });
      // NẮP KHỚP VAI: che khe hở khi đấm/ chưởng (xem ARM_CAP_Z — LUÔN sau thân)
      rig.addPart({
        bone: `shoulder${side}`, z: ARM_CAP_Z, slot: 'joints',
        key: factory.get({
          shape: 'joint', variant: 'shoulder', color: armPart.color, accent: 0, skin: 0,
          w: Math.max(6, armPart.w * armFit.scale * 0.55), h: Math.max(6, armPart.w * armFit.scale * 0.55),
        }),
        ox: 0, oy: 0, sx: 1, sy: 1,
      });
    }
  } else {
    for (const side of ['L', 'R'] as const) {
      const az = armZ(side);
      rig.addPart({ bone: `shoulder${side}`, z: az, slot: 'arms', key: ai.upperArm!.key, ox: -ai.upperArm!.topX * armFit.scale, oy: -ai.upperArm!.topY * armFit.scale, sx: armFit.scale, sy: armFit.scale });
      rig.addPart({ bone: `elbow${side}`, z: az + 1, slot: 'arms', key: ai.foreArm!.key, ox: -ai.foreArm!.topX * foreArmFit!.scale, oy: -ai.foreArm!.topY * foreArmFit!.scale, sx: foreArmFit!.scale, sy: foreArmFit!.scale });
      rig.addPart({
        bone: `shoulder${side}`, z: ARM_CAP_Z, slot: 'joints',
        key: factory.get({ shape: 'joint', variant: 'shoulder', color: ai.upperArm!.color, accent: 0, skin: 0, w: Math.max(6, ai.upperArm!.w * armFit.scale * 0.55), h: Math.max(6, ai.upperArm!.w * armFit.scale * 0.55) }),
        ox: 0, oy: 0, sx: 1, sy: 1,
      });
      rig.addPart({
        bone: `elbow${side}`, z: ARM_CAP_Z + 1, slot: 'joints',
        key: factory.get({ shape: 'joint', variant: 'elbow', color: ai.foreArm!.color, accent: 0, skin: 0, w: Math.max(6, ai.foreArm!.w * foreArmFit!.scale * 0.5), h: Math.max(6, ai.foreArm!.w * foreArmFit!.scale * 0.5) }),
        ox: 0, oy: 0, sx: 1, sy: 1,
      });
    }
  }

  // ---- BÀN TAY: NAY CỐ Ý KHÔNG DÙNG (nhánh này chỉ còn là lưới an toàn cho sheet có ô `hand`).
  //
  // Người dùng chốt: *"bỏ bàn tay, thay cánh tay bằng cả cánh tay ở ảnh 3x3 dòng 2 cột 1"*.
  // Ô 4 (r2c1) ĐÃ vẽ sẵn nắm đấm ở cuối tay, còn ô 8 (r3c2) là bàn tay XOÈ ⇒ `CELL_ORDER` để ô 8
  // thành `empty` nên `ai.hand` là `undefined` và khối này KHÔNG chạy. Giữ lại chỉ để nếu sau này
  // có sheet trả về ô `hand` thì vẫn dán được — KHÔNG bật lại bằng cách gán ô 8 (sẽ thành 2 bàn tay).
  if (ai.hand) {
    const handFit = fit(ai.hand.h, torsoLen * 0.24);
    for (const side of ['L', 'R'] as const) {
      rig.addPart({
        bone: `shoulder${side}`, z: 28, slot: 'hands', key: ai.hand.key,
        // đặt ĐIỂM KHỚP TRÊN của bàn tay trùng ĐIỂM CUỐI của cánh tay
        ox: armEndX - ai.hand.topX * handFit.scale,
        oy: armEndY - ai.hand.topY * handFit.scale,
        sx: handFit.scale, sy: handFit.scale,
      });
    }
  }

  // ---- đầu
  // ĐẦU: gắn điểm ĐÁY đầu (cằm) vào xương cổ, rồi HẠ XUỐNG `headSink` cho cằm cắm vào vai.
  rig.addPart({ bone: 'head', z: 50, slot: 'head', key: ai.head.key, ox: -ai.head.botX, oy: -ai.head.botY + headSink, sx: 1, sy: 1 });

  /*
   * CHUẨN HOÁ CHIỀU CAO TOÀN THÂN — bước bắt buộc.
   * Mảnh AI cao ~300px còn nhân vật trong game chỉ ~100px. Nếu không co lại thì
   * nhân vật AI sẽ to gấp 3 lần nhân vật vector (đã từng bị đúng như vậy).
   */
  /*
   * ⚠️⚠️ `LEG_DROP` PHẢI ĐƯỢC TÍNH VÀO CHIỀU CAO GỐC — NẾU KHÔNG THÌ MỌI LẦN "HẠ CHÂN" ĐỀU BỊ
   * TRIỆT TIÊU.
   *
   * LỖI THẬT ĐÃ ĐO: tôi tăng `LEG_DROP` 5 → 15 → 20 → **30** mà người dùng vẫn báo *"tôi không
   * thấy thay đổi gì"*. Đo xương: `localHipLen` chỉ đổi từ -32,1 xuống -30,3 (dịch **1,8px** thay vì
   * 30px).
   *
   * NGUYÊN NHÂN: `legY = hipY + LEG_DROP` đẩy chân xuống, nhưng `nativeHeight` ở dưới KHÔNG biết
   * điều đó ⇒ hệ số chuẩn hoá `k = target / nativeHeight` giữ nguyên, trong khi tổng chiều cao THẬT
   * đã tăng thêm `LEG_DROP` ⇒ rig bị co lại đúng bằng phần vừa nới ⇒ **hạ bao nhiêu cũng như không**.
   * Càng tăng `LEG_DROP` thì `k` càng nhỏ, triệt tiêu càng nhiều — đúng hiện tượng người dùng thấy.
   *
   * Nay cộng `LEG_DROP` vào `nativeHeight` ⇒ chiều cao gốc phản ánh ĐÚNG thân + chân đã hạ, nên
   * `k` bù đúng và phần hạ chân GIỮ ĐƯỢC trọn vẹn.
   */
  /*
   * ⚠️⚠️ `headSink` PHẢI ĐƯỢC TÍNH VÀO CHIỀU CAO GỐC — cùng lỗi với `LEG_DROP` trước đây.
   *
   * Đo thật: tăng `headSink` 10 → 15 muốn "tách thêm 5px", nhưng khoảng cách chỉ đi 5 → 6px, vì
   * bước chuẩn hoá `k = target / nativeHeight` KHÔNG biết đầu đã bị đẩy xuống ⇒ `k` co nhỏ lại và
   * triệt tiêu gần hết phần nới. Cộng `headSink` vào `nativeHeight` thì `k` bù đúng, khoảng cách
   * tách đầu↔thân GIỮ ĐƯỢC đúng con số người dùng yêu cầu.
   */
  const nativeHeight = legLen + LEG_DROP + bootH * 0.45 + torsoLen + headH;
  const target = (opts.targetHeight ?? TARGET_CHAR_HEIGHT) * (opts.scaleMult ?? 1);
  const k = clamp(target / Math.max(1, nativeHeight), 0.05, 2);
  for (const b of rig.bones.values()) {
    b.x *= k;
    b.y *= k;
  }
  for (const p of rig.parts) {
    p.ox *= k;
    p.oy *= k;
    p.sx *= k;
    p.sy *= k;
  }
  rig.nativeScale = k;

  return rig;
}

/** Dựng TƯỚNG từ ảnh AI (ảnh class, hoặc ảnh "dáng" của tab Trang phục). */
export function buildAiCharacterRig(
  factory: PartFactory,
  spec: CharacterSpec,
  ai: AiPartSet,
  opts: { scaleMult?: number } = {},
): Rig {
  void spec;
  /*
   * `scaleMult` dùng cho ULTIMATE BIẾN HÌNH (Dã trư ×1.8 — người dùng chốt). Mặc định 1 ⇒ mọi
   * chỗ gọi cũ giữ nguyên hành vi.
   */
  return buildAiRigFromParts(factory, ai, { targetHeight: TARGET_CHAR_HEIGHT, scaleMult: opts.scaleMult ?? 1 });
}

/**
 * Dựng QUÁI từ ảnh AI.
 *
 * Chiều cao mục tiêu suy từ `radius` — ĐÚNG công thức mà rig vector đang dùng
 * (`scale = radius/20`), để quái AI và quái vector đứng cạnh nhau không lệch cỡ.
 * Boss (radius 34) vì thế cao gấp đôi quái thường (radius 17) — đúng ý đồ thiết kế.
 */
export function buildAiEnemyRigFromParts(
  factory: PartFactory,
  ai: AiPartSet,
  opts: { radius: number; isBoss?: boolean; scaleMult?: number },
): Rig {
  /*
   * ⚠️ `scaleMult` LÀ HÌNH ẢNH, KHÔNG PHẢI CHỈ SỐ: bán kính va chạm (`radius`) vẫn do sim quyết định.
   * Dùng cho BOSS CUỐI (titan) — yêu cầu người dùng *"boss cuối khổng lồ"*; xem `AI_ENEMY_DRAW_SCALE`.
   */
  const scale = Math.max(0.55, opts.radius / 20) * (opts.scaleMult ?? 1);
  const targetHeight = 100 * scale;
  const rig = buildAiRigFromParts(factory, ai, { targetHeight });
  void opts.isBoss;
  return rig;
}
